import { $$, $ } from '../utils/dom.js';
import { setHtmlClass } from '../utils/html-class.js';

const NAV_OPEN_CLASS = 'nav-open';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const NAV_BASE_DELAY = 250;
const NAV_STAGGER = 80;

function getFocusableElements(container) {
  return $$(FOCUSABLE_SELECTOR, container).filter((element) => !element.disabled);
}

function getNavAnimatedItems(panel) {
  const links = $$('.nav__link', panel);
  const visit = $('.nav__visit', panel);

  return visit ? [...links, visit] : links;
}

function setNavItemDelays(panel) {
  getNavAnimatedItems(panel).forEach((item, index) => {
    item.style.setProperty(
      '--nav-item-delay',
      `${NAV_BASE_DELAY + index * NAV_STAGGER}ms`,
    );
  });
}

function resetNavItemDelays(panel) {
  getNavAnimatedItems(panel).forEach((item) => {
    item.style.removeProperty('--nav-item-delay');
  });
}

export function initNav() {
  const disposers = $$('[data-component="nav"]').map((root) => setupNav(root));

  return () => {
    disposers.forEach((dispose) => {
      dispose();
    });
  };
}

function setupNav(root) {
  const panel = $('[data-nav-panel]', root);
  const openBtn = $('[data-nav-open]', root);
  const closeBtn = $('[data-nav-close]', root);

  if (!panel || !openBtn || !closeBtn) {
    return () => {};
  }

  let removeKeydown = null;
  let previousFocus = null;

  const close = () => {
    if (panel.getAttribute('data-state') !== 'open') {
      return;
    }

    if (removeKeydown) {
      removeKeydown();
      removeKeydown = null;
    }

    panel.setAttribute('data-state', 'closed');
    resetNavItemDelays(panel);
    panel.setAttribute('hidden', '');
    setHtmlClass(NAV_OPEN_CLASS, false);
    openBtn.setAttribute('aria-expanded', 'false');

    if (previousFocus && typeof previousFocus.focus === 'function') {
      previousFocus.focus();
    }

    previousFocus = null;
  };

  const open = () => {
    previousFocus = document.activeElement;
    panel.removeAttribute('hidden');
    setNavItemDelays(panel);
    panel.setAttribute('data-state', 'open');
    setHtmlClass(NAV_OPEN_CLASS, true);
    openBtn.setAttribute('aria-expanded', 'true');

    const onKeydown = (event) => {
      if (event.key === 'Escape') {
        close();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = getFocusableElements(panel);
      if (!focusable.length) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeydown);
    removeKeydown = () => {
      document.removeEventListener('keydown', onKeydown);
    };

    closeBtn.focus();
  };

  const onOpen = () => {
    open();
  };

  const onClose = () => {
    close();
  };

  const onBackdrop = (event) => {
    if (event.target === panel) {
      close();
    }
  };

  openBtn.addEventListener('click', onOpen);
  closeBtn.addEventListener('click', onClose);
  panel.addEventListener('click', onBackdrop);

  return () => {
    close();
    openBtn.removeEventListener('click', onOpen);
    closeBtn.removeEventListener('click', onClose);
    panel.removeEventListener('click', onBackdrop);
  };
}
